/**
 * Battle March scenario data for the General's Adjutant.
 *
 * Source: Warhammer: the Old World - Battle March rules PDF (pp. 4-7) and the
 * Battle March General's Companion (pp. 12, 22-25). Both are image-only scans;
 * the OCR is in src/docs/battle-march/. Every stat line below was read off the
 * page images directly - OCR misread the Wandering Mercenary's Weapon Skill and
 * Leadership, and garbled the Enraged Troll entirely.
 */
import type { Profile } from '../types/faction';

export interface D6TableEntry {
  /** Faces this result covers, e.g. [1, 2] for a 1-2 row */
  roll: number[];
  name: string;
  effect: string;
}

export interface BattleMarchProfile {
  name: string;
  troop_type: string;
  base_size: string;
  unit_size: string;
  armour_value?: string;
  equipment: string[];
  special_rules: string[];
  profiles: { name: string; profile: Profile }[];
  /** Rules printed with the entry rather than in the main rules index */
  rules_text: { name: string; text: string }[];
}

// ---------------------------------------------------------------------------
// Battlefield and objectives (rules PDF pp. 4-5)
// ---------------------------------------------------------------------------

export const BATTLEFIELD = {
  min: '30" x 44"',
  max: '36" x 48"',
  guidance:
    'The smaller size suits armies of 400-600 points, the larger armies of more than 600 points. ' +
    'Avoid terrain measuring more than 12" at its widest point.',
} as const;

/** Rolled once terrain is placed, before armies are deployed */
export const OBJECTIVES_TABLE: D6TableEntry[] = [
  { roll: [1, 2], name: 'Two treasure troves', effect: 'Place two treasure troves.' },
  { roll: [3, 4], name: 'Three treasure troves', effect: 'Place three treasure troves.' },
  { roll: [5, 6], name: 'One strategic landmark', effect: 'Place one strategic landmark.' },
];

export const UNUSUAL_PROPERTIES_TABLE: D6TableEntry[] = [
  {
    roll: [1, 2],
    name: 'Magic in the Air',
    effect: 'The controlling unit gains the Magic Resistance (-2) special rule.',
  },
  {
    roll: [3, 4],
    name: 'Righteous Zeal',
    effect: 'The controlling unit gains the Frenzy special rule.',
  },
  {
    roll: [5, 6],
    name: '"We’re Not Leaving"',
    effect: 'The controlling unit gains the Stubborn special rule.',
  },
];

export const OBJECTIVE_RULES = {
  treasureTrove:
    'A 40mm round base treated as battlefield decoration. Placed as shown on the deployment maps, ' +
    'but not within 3" of a terrain feature or straddling a low linear obstacle; move the terrain by the ' +
    'smallest possible amount if needed.',
  strategicLandmark:
    'A 100mm round base placed in the centre of the battlefield. Impassable terrain over which no line of ' +
    'sight can be drawn. Roll once on the Unusual Properties table before deployment; the controlling unit ' +
    'benefits from that property until the end of the next turn.',
  control:
    'At the end of each player’s turn a single unit can control each objective. It must be within 3" and ' +
    'have a Unit Strength of 5 or more. Units that are fleeing or that have succumbed to Stupidity cannot ' +
    'control an objective. If two or more eligible units are within 3", the closest controls it; if equally ' +
    'close, the higher Unit Strength; if still tied, the objective is contested and neither controls it.',
} as const;

// ---------------------------------------------------------------------------
// Deployment (rules PDF p. 6)
// ---------------------------------------------------------------------------

/**
 * The six maps, with the dimensions printed on the diagrams. Measurements were
 * read off the page images (rules PDF pp. 6-7); the drawings assume the minimum
 * 44" x 30" battlefield, which is what every printed figure adds up to.
 */
export interface DeploymentMap extends D6TableEntry {
  id: string;
  /** Dimensions printed on the map, for display under the diagram */
  dimensions: string;
  /** Whether the book allows this map to be mirrored left to right */
  mirrorable: boolean;
}

export const DEPLOYMENT_MAPS: DeploymentMap[] = [
  {
    roll: [1], id: 'pitched_battle', name: 'Pitched Battle',
    effect: 'A full-width band along each long board edge, with 15" of no-man’s-land between them.',
    dimensions: 'Zones 7.5" deep; 7.5" + 7.5" between them', mirrorable: false,
  },
  {
    roll: [2], id: 'close_encounter', name: 'Close Encounter',
    effect: 'Opposite quarters of the battlefield, with a 15" circle at the centre that neither army deploys into.',
    dimensions: '15" central circle', mirrorable: true,
  },
  {
    roll: [3], id: 'opposed_flanks', name: 'Opposed Flanks',
    effect: 'Two wedges running the length of the battlefield, each 18" deep at opposite ends and tapering to nothing.',
    dimensions: '18" deep at the deep end', mirrorable: true,
  },
  {
    roll: [4], id: 'meeting_engagement', name: 'Meeting Engagement',
    effect: 'Staggered bands along each long edge, each stopping 11" short of the opposite end.',
    dimensions: 'Zones 7.5" deep, offset 11"; 7.5" + 7.5" between them', mirrorable: true,
  },
  {
    roll: [5], id: 'mountain_pass', name: 'Mountain Pass',
    effect: 'A band along each short board edge, with 22" of no-man’s-land between them.',
    dimensions: 'Zones 11" wide; 11" + 11" between them', mirrorable: false,
  },
  {
    roll: [6], id: 'outflank', name: 'Outflank',
    effect: 'Two diagonal zones in opposite corners, separated by a diagonal band 22" wide along each long edge.',
    dimensions: '22" along the top and bottom edges', mirrorable: true,
  },
];

export const DEPLOYMENT_RULES = {
  order:
    'One player rolls for the map. Their opponent chooses their deployment zone. The winner of a roll-off ' +
    'deploys the first unit, then armies are deployed using the alternating units method.',
  mirroring:
    'Close Encounter, Opposed Flanks, Meeting Engagement and Outflank may all be reversed left to right. ' +
    'If in doubt, roll a D6 and let fate decide.',
  firstTurn: 'Once deployment is complete, the winner of a roll-off chooses which player takes the first turn.',
  gameLength: 'Five rounds, until one side concedes, or until the agreed time limit is reached.',
} as const;

// ---------------------------------------------------------------------------
// Victory Points (rules PDF p. 7)
// ---------------------------------------------------------------------------

export interface VictoryPointSource {
  name: string;
  vp: string;
  detail: string;
  /** Scored repeatedly at the end of each player's turn rather than once at the end */
  perTurn?: boolean;
}

export const VICTORY_POINTS: VictoryPointSource[] = [
  { name: 'Dead or Fled', vp: 'as normal', detail: 'Scored for destroying, routing or significantly reducing enemy units, as the rulebook.' },
  { name: 'The King is Dead', vp: '+50', detail: 'The enemy General is slain, has fled the battlefield, or is fleeing when the game ends.' },
  { name: 'Trophies of War', vp: '+25', detail: 'For every enemy standard claimed as a trophy.' },
  { name: 'Trophies of War (Battle Standard)', vp: '+25', detail: 'The enemy Battle Standard Bearer is slain, has fled, or is fleeing when the game ends.' },
  { name: 'Treasure Troves', vp: '+10', detail: 'For each treasure trove controlled, at the end of each player’s turn.', perTurn: true },
  { name: 'Strategic Landmarks', vp: '+25', detail: 'If a player controls the strategic landmark, at the end of each player’s turn.', perTurn: true },
];

// ---------------------------------------------------------------------------
// Secondary objectives (General's Companion p. 12)
// ---------------------------------------------------------------------------

export const SECONDARY_OBJECTIVES = [
  {
    id: 'raid_and_burn',
    name: 'Raid & Burn',
    summary: 'Treasure troves can be destroyed rather than held.',
    rules:
      'A unit with a Unit Strength of 5 or more can attempt to destroy a treasure trove by moving into base ' +
      'contact with it during the Remaining Moves sub-phase. While destroying a trove the unit cannot shoot ' +
      'and can only cast spells with a range of ‘Combat’ or ‘Self’. If at the beginning of its next Start of ' +
      'Turn sub-phase the unit is still in base contact, still has a Unit Strength of 5 or more, is not engaged ' +
      'in combat and is not fleeing, the trove is destroyed and removed.',
    victoryPoints: 'The controlling player wins a bonus of 30 Victory Points for each treasure trove destroyed.',
  },
  {
    id: 'baggage_carts',
    name: 'Baggage Carts',
    summary: 'One or both players add a baggage cart for the duration of the game.',
    rules:
      'Attack the Baggage: if only one player has a baggage cart, they are the defender and their opponent is ' +
      'the attacker. Guard Duty: if both players have one, the victor loots the enemy supplies.',
    victoryPoints:
      'If a baggage cart is alive on the battlefield at the end of the game its controlling player wins 25 ' +
      'Victory Points; otherwise their opponent wins 25. In addition, 25 more if it is completely within the ' +
      'enemy’s deployment zone at the end of the game, and 25 more if it escapes from the battlefield.',
  },
] as const;

// ---------------------------------------------------------------------------
// Profiles (General's Companion pp. 22, 24, 25) - all read from the page images
// ---------------------------------------------------------------------------

const dash = '-';

export const BAGGAGE_CART: BattleMarchProfile = {
  name: 'Baggage Cart',
  troop_type: 'Heavy Chariot',
  base_size: '60 x 100 mm',
  unit_size: '1',
  armour_value: '5+',
  equipment: ['Driver: Hand weapon', 'Draft Animals: Hooves, horns, tusks or similar (hand weapons)'],
  special_rules: ['close_order', 'flee_to_safety', 'loner', 'non_combatant'],
  profiles: [
    { name: 'Baggage Cart', profile: { M: dash, WS: dash, BS: dash, S: 4, T: 5, W: 4, I: dash, A: dash, Ld: dash } },
    { name: 'Driver', profile: { M: dash, WS: 2, BS: 2, S: 3, T: dash, W: dash, I: 3, A: 1, Ld: 6 } },
    { name: 'Draft Animal (x2)', profile: { M: 6, WS: 2, BS: dash, S: 3, T: dash, W: dash, I: 3, A: 1, Ld: dash } },
  ],
  rules_text: [
    {
      name: 'Flee to Safety',
      text:
        'Should any part of a baggage cart move into contact with, or cross beyond, a section of board edge ' +
        'that is within their opponent’s deployment zone, it is removed from play but is not destroyed. The ' +
        'unit has escaped the battle and is on its way to safety.',
    },
    {
      name: 'Non-Combatant',
      text:
        'A model with this special rule cannot Charge, make a pursuit or overrun move, or follow up. It also ' +
        'cannot use the Inspiring Presence rule of the army’s General nor the Hold Your Ground rule of a ' +
        'Battle Standard.',
    },
  ],
};

export const ENRAGED_TROLL: BattleMarchProfile = {
  name: 'Enraged Troll',
  troop_type: 'Monstrous infantry',
  base_size: '40 x 40 mm',
  unit_size: '1',
  equipment: ['Hand weapon', 'Troll vomit', 'Calloused hide (counts as light armour)'],
  special_rules: ['armour_bane_1', 'fear', 'flammable', 'regeneration_5', 'skirmishers'],
  profiles: [
    { name: 'Enraged Troll', profile: { M: 6, WS: 3, BS: 1, S: 5, T: 4, W: 3, I: 10, A: 3, Ld: 5 } },
  ],
  rules_text: [
    {
      name: 'Troll vomit',
      text:
        'Combat, S, AP -2. A Troll in base contact with an enemy model may make one additional attack each ' +
        'turn with this weapon. It must be made last, after all other attacks including Stomp Attacks, but ' +
        'hits automatically.',
    },
  ],
};

export const WANDERING_MERCENARIES: BattleMarchProfile = {
  name: 'Wandering Mercenaries',
  troop_type: 'Regular infantry',
  base_size: '25 x 25 mm',
  unit_size: '10',
  equipment: ['Hand weapons and shields, or hand weapons and shortbows'],
  special_rules: ['mercenaries', 'move_through_cover', 'skirmishers'],
  profiles: [
    { name: 'Wandering Mercenary', profile: { M: 4, WS: 3, BS: 3, S: 3, T: 3, W: 1, I: 3, A: 1, Ld: 7 } },
  ],
  rules_text: [
    {
      name: 'Arrival',
      text:
        'Arrives immediately as reinforcements from any point on the battlefield edge, not within either ' +
        'player’s deployment zone. For the purposes of calculating Victory Points this unit is worth 50 points.',
    },
  ],
};

// ---------------------------------------------------------------------------
// Random Happenings (General's Companion pp. 23-25)
// ---------------------------------------------------------------------------

export interface RandomHappeningsTable {
  id: string;
  name: string;
  intro: string;
  /** When the roll is made and by whom */
  when: string;
  results: D6TableEntry[];
}

export const RANDOM_HAPPENINGS: RandomHappeningsTable[] = [
  {
    id: 'disruptive_weather',
    name: 'Disruptive Weather',
    intro: 'A game-long effect that changes the pace and flow of the battle.',
    when: 'Roll a D6 before deployment begins. The result remains in play for the duration of the game.',
    results: [
      { roll: [1], name: 'Heavy Downpour', effect: 'All units suffer a -1 modifier to their Movement characteristic (to a minimum of 1), and units with Swiftstride are unable to use it. Units with Fly (X) are also affected.' },
      { roll: [2], name: 'Fierce Gale', effect: 'Reduce the range of all missile weapons with a Strength of 5 or less by 6" (to a minimum of 3").' },
      { roll: [3], name: 'Scorching Heat', effect: 'At the beginning of each Start of Turn sub-phase the active player rolls a D6 for each unit in their army. On a 1 that unit has succumbed: D3 models must pass a Toughness test or lose a single Wound. Units wearing heavy armour, full plate armour or barding succumb on a 1 or 2.' },
      { roll: [4], name: 'Lightning Strikes', effect: 'At the beginning of each Start of Turn sub-phase the active player rolls a D6. On a 1-2, lightning strikes a single randomly determined unit belonging to that player, which suffers D6 Strength 4 hits, each with an AP of -1.' },
      { roll: [5], name: 'Dust Devil', effect: 'Place a 3" blast template in the centre of the battlefield and scatter it 2D6". It scatters D6" during every Start of Turn sub-phase. Should it scatter onto a unit, move it the smallest amount possible so it touches no bases. Any unit that moves through the dust devil, or that it moves over, suffers a -1 modifier to its Weapon Skill and Ballistic Skill (to a minimum of 1) until the next Start of Turn sub-phase.' },
      { roll: [6], name: 'Freezing Winds', effect: 'At the beginning of each Start of Turn sub-phase the active player rolls a D6 for each unit in their army. On a 2+ the unit acts as normal; on a 1 it must make a Leadership test before moving, shooting, casting spells or declaring a charge. If failed, the unit huddles against the cold and refuses to act. Compulsory moves, such as fleeing or Giving Ground, are unaffected.' },
    ],
  },
  {
    id: 'wilderness_terrain',
    name: 'Wilderness Terrain',
    intro: 'Reinforces the danger, and the occasional blessing, of the untamed wilds.',
    when: 'Units that end their movement within any natural terrain feature, or that are deployed within one, roll a D6 to discover its properties.',
    results: [
      { roll: [1], name: 'Briar Patch', effect: 'A briar patch is difficult terrain. Units that march, charge, flee or pursue through it treat it as dangerous terrain.' },
      { roll: [2], name: 'Defensive Position', effect: 'A unit occupying this terrain feature counts as defending a low linear obstacle. Any unit that charges a unit occupying it becomes Disrupted.' },
      { roll: [3], name: 'Toxic Spores', effect: 'Each model that ends its Movement completely within this terrain feature must make a Toughness test or lose a single Wound.' },
      { roll: [4], name: 'Lingering Magic', effect: 'Units within this terrain feature gain Magic Resistance (-1).' },
      { roll: [5], name: 'Healing Herbs', effect: 'Immediately, and during every subsequent Start of Turn sub-phase, a single model within this terrain feature that is not engaged in combat, not fleeing and has not succumbed to Stupidity regains a single lost Wound.' },
      { roll: [6], name: 'Troll’s Lair', effect: 'Place an Enraged Troll in base contact with the front arc of the discovering unit; if a unit of Skirmishers discovered it, form them up first. The Troll counts as having charged. Immediately fight a round of combat. If the unit wins, the Troll is removed from play and the unit restrains and reforms. If the Troll wins it does not follow up or pursue, returning to its lair to defend it again if disturbed.' },
    ],
  },
  {
    id: 'chaos_of_war',
    name: 'The Chaos of War',
    intro: 'The truly haphazard nature of war, from stray baggage carts to wandering mercenaries.',
    when: 'From their second turn onwards, the player who took the first turn rolls a D6 at the beginning of each of their Start of Turn sub-phases. If the number rolled is equal to or lower than their current turn number, their opponent rolls a D6 on this table, re-rolling any result rolled previously.',
    results: [
      { roll: [1], name: 'Aetheric Instability', effect: 'Whenever a player makes a Casting roll, any natural double 1, 2 or 3 results in a miscast, and any natural double 4, 5 or 6 results in a perfect invocation.' },
      { roll: [2], name: 'Misplaced Resources', effect: 'Roll a Scatter dice in the centre of the table and follow the arrow to the battlefield edge; this is where a lost baggage cart arrives. The winner of a roll-off owns the cart and must reclaim and defend it; their opponent must attack and destroy it.' },
      { roll: [3], name: 'Dwindling Ammunition', effect: 'Units armed with missile weapons can only shoot them every second turn. A unit that shoots during its first turn cannot shoot that weapon again until its third turn.' },
      { roll: [4], name: 'Treasures of the Backcountry', effect: 'For this battle, treasure troves are worth 20 Victory Points and strategic landmarks are worth 50 Victory Points.' },
      { roll: [5], name: 'Drawn-out Conflict', effect: 'Unless one side concedes or the agreed time limit is reached, this game lasts for six rounds rather than the usual five.' },
      { roll: [6], name: 'Wandering Mercenaries', effect: 'The winner of a roll-off gains a unit of ten Wandering Mercenaries, armed either with hand weapons and shields or hand weapons and shortbows. On a tie, both players gain a unit.' },
    ],
  },
];

export const RANDOM_HAPPENINGS_USAGE =
  'Before the game, players decide whether to include Random Happenings. If so they choose one table; if they ' +
  'cannot agree, roll a D3. Using two or all three at once is allowed, adding further variety and chaos.';
